import { LanguageProvider } from "./i18n/LanguageProvider";
import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router";
import { ThemeProvider } from "./components/theme/ThemeProvider";
import { AuthProvider } from "./components/auth/AuthProvider";
import { DialogProvider } from "./components/ui/Dialog";
import { RequireAuth } from "./components/auth/RequireAuth";
import { RequireGuest } from "./components/auth/RequireGuest";
import { RequireRole } from "./components/auth/RequireRole";
import { RequirePermission } from "./components/auth/RequirePermission";
import { RequireFeature } from "./components/auth/RequireFeature";
import { RequireActive } from "./components/auth/RequireActive";
import { AppShell } from "./components/layout/AppShell";
import { LoginPage } from "./routes/LoginPage";
import { MaintenancePage } from "./routes/MaintenancePage";
import { RegisterPage } from "./routes/RegisterPage";
import { ForgotPasswordPage } from "./routes/ForgotPasswordPage";
import { ResetPasswordPage } from "./routes/ResetPasswordPage";
import { NotFoundPage } from "./routes/NotFoundPage";
import { StreamingLayout } from "./components/streaming/StreamingLayout";
import { StreamingFlyoutHost } from "./components/streaming/StreamingFlyoutHost";
import { ControlCenterLayout } from "./components/streaming/control-center/ControlCenterLayout";
import { MediaCanvasLayout } from "./components/streaming/control-center/media/MediaCanvasLayout";
import { DELETE_PROTECTION_PERMISSION } from "./lib/acl/permissions";

const ProfilePage = lazy(() => import("./routes/ProfilePage").then(m => ({ default: m.ProfilePage })));
const PackagesRedirectPage = lazy(() => import("./routes/PackagesRedirectPage").then(m => ({ default: m.PackagesRedirectPage })));
const DashboardPage = lazy(() => import("./routes/DashboardPage").then(m => ({ default: m.DashboardPage })));
const SystemDashboardPage = lazy(() => import("./routes/SystemDashboardPage").then(m => ({ default: m.SystemDashboardPage })));
const StreamingDashboardPage = lazy(() => import("./routes/StreamingDashboardPage").then(m => ({ default: m.StreamingDashboardPage })));
const GeneralSettingsPage = lazy(() => import("./routes/GeneralSettingsPage").then(m => ({ default: m.GeneralSettingsPage })));
const EmailSettingsPage = lazy(() => import("./routes/EmailSettingsPage").then(m => ({ default: m.EmailSettingsPage })));
const SessionSettingsPage = lazy(() => import("./routes/SessionSettingsPage").then(m => ({ default: m.SessionSettingsPage })));
const SecuritySettingsPage = lazy(() => import("./routes/SecuritySettingsPage").then(m => ({ default: m.SecuritySettingsPage })));
const RegistrationSettingsPage = lazy(() => import("./routes/RegistrationSettingsPage").then(m => ({ default: m.RegistrationSettingsPage })));
const UserRegistrationPage = lazy(() => import("./routes/UserRegistrationPage").then(m => ({ default: m.UserRegistrationPage })));
const UserAdminPage = lazy(() => import("./routes/UserAdminPage").then(m => ({ default: m.UserAdminPage })));
const UserGroupsPage = lazy(() => import("./routes/UserGroupsPage").then(m => ({ default: m.UserGroupsPage })));
const UserRolesPage = lazy(() => import("./routes/UserRolesPage").then(m => ({ default: m.UserRolesPage })));
const WebConsolePage = lazy(() => import("./routes/WebConsolePage").then(m => ({ default: m.WebConsolePage })));
const ApiSettingsPage = lazy(() => import("./routes/ApiSettingsPage").then(m => ({ default: m.ApiSettingsPage })));
const SslControlPage = lazy(() => import("./routes/SslControlPage").then(m => ({ default: m.SslControlPage })));
const VpnControlPage = lazy(() => import("./routes/VpnControlPage").then(m => ({ default: m.VpnControlPage })));
const ChangelogPage = lazy(() => import("./routes/ChangelogPage").then(m => ({ default: m.ChangelogPage })));
const NewsPage = lazy(() => import("./routes/NewsPage").then(m => ({ default: m.NewsPage })));
const NetworkPage = lazy(() => import("./routes/NetworkPage").then(m => ({ default: m.NetworkPage })));
const TransfersPage = lazy(() => import("./routes/TransfersPage").then(m => ({ default: m.TransfersPage })));
const SystemLogsPage = lazy(() => import("./routes/SystemLogsPage").then(m => ({ default: m.SystemLogsPage })));
const DeleteProtectionPage = lazy(() => import("./routes/DeleteProtectionPage").then(m => ({ default: m.DeleteProtectionPage })));
const AuditLogPage = lazy(() => import("./routes/AuditLogPage").then(m => ({ default: m.AuditLogPage })));
const MediaLibraryPage = lazy(() => import("./routes/MediaLibraryPage").then(m => ({ default: m.MediaLibraryPage })));
const MediaRequestsPage = lazy(() => import("./routes/MediaRequestsPage").then(m => ({ default: m.MediaRequestsPage })));
const MediaRequestDetailPage = lazy(() => import("./routes/MediaRequestDetailPage").then(m => ({ default: m.MediaRequestDetailPage })));
const NotificationSettingsPage = lazy(() => import("./routes/NotificationSettingsPage").then(m => ({ default: m.NotificationSettingsPage })));
const AppTemplatesPage = lazy(() => import("./routes/AppTemplatesPage").then(m => ({ default: m.AppTemplatesPage })));
const ControlCenterPage = lazy(() => import("./routes/ControlCenterPage").then(m => ({ default: m.ControlCenterPage })));
const ServerUsersPage = lazy(() => import("./routes/ServerUsersPage").then(m => ({ default: m.ServerUsersPage })));
const UserDetailPage = lazy(() => import("./routes/UserDetailPage").then(m => ({ default: m.UserDetailPage })));
const DevicesPage = lazy(() => import("./routes/DevicesPage").then(m => ({ default: m.DevicesPage })));
const HistoryAnalyticsPage = lazy(() => import("./routes/HistoryAnalyticsPage").then(m => ({ default: m.HistoryAnalyticsPage })));
const MediaPortalPage = lazy(() => import("./routes/MediaPortalPage").then(m => ({ default: m.MediaPortalPage })));
const MediaItemPage = lazy(() => import("./routes/MediaItemPage").then(m => ({ default: m.MediaItemPage })));
const MediaLibraryDetailPage = lazy(() => import("./routes/MediaLibraryDetailPage").then(m => ({ default: m.MediaLibraryDetailPage })));
// MirrorViewerPage removed -- mirror now renders as floating dock in StreamingLayout

const App = () => (
  <LanguageProvider><ThemeProvider>
    <DialogProvider>
      <AuthProvider>
        <Suspense fallback={<div />}>
        <Routes>
          <Route element={<RequireGuest />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>
          <Route path="/maintenance" element={<MaintenancePage />} />
          <Route element={<RequireAuth />}>
            <Route element={<AppShell />}>
              <Route path="/" element={<Navigate to="/profile" replace />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/notifications" element={<Navigate to="/profile?tab=notification-center" replace />} />
              <Route element={<RequireActive />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                {/* OAuth bounce-back alias — backend redirects to /system/packages?rcloneSession=... */}
                <Route path="/system/packages" element={<PackagesRedirectPage />} />
                <Route element={<RequireRole role="admin" />}>
                  <Route path="/system/dashboard" element={<SystemDashboardPage />} />
                  <Route element={<StreamingLayout />}>
                    <Route path="/streaming/dashboard" element={<StreamingDashboardPage />} />
                    <Route path="/streaming/dashboard/mirror/:mirrorId" element={<Navigate to="/streaming/dashboard" replace />} />
                    <Route element={<ControlCenterLayout />}>
                      <Route path="/streaming/dashboard/control-center" element={<ControlCenterPage />} />
                      <Route path="/streaming/dashboard/control-center/server-users" element={<ServerUsersPage />} />
                      <Route path="/streaming/dashboard/control-center/server-users/:serverType/:userId" element={<UserDetailPage />} />
                      <Route path="/streaming/dashboard/control-center/devices" element={<DevicesPage />} />
                      <Route path="/streaming/dashboard/control-center/history" element={<HistoryAnalyticsPage />} />
                    </Route>
                    <Route element={<MediaCanvasLayout />}>
                      <Route path="/streaming/dashboard/control-center/media" element={<MediaPortalPage />} />
                      <Route path="/streaming/dashboard/control-center/media/item/:serverType/:itemId" element={<MediaItemPage />} />
                      <Route path="/streaming/dashboard/control-center/media/library/:serverType/:libraryId" element={<MediaLibraryDetailPage />} />
                    </Route>
                  </Route>
                  <Route path="/streaming/control-center" element={<Navigate to="/streaming/dashboard/control-center" replace />} />
                  <Route path="/settings/general" element={<GeneralSettingsPage />} />
                  <Route path="/settings/email" element={<EmailSettingsPage />} />
                  <Route path="/settings/notifications" element={<NotificationSettingsPage />} />
                  <Route path="/settings/email/templates" element={<Navigate to="/settings/email?tab=templates" replace />} />
                  <Route path="/settings/sessions" element={<SessionSettingsPage />} />
                  <Route path="/settings/users" element={<Navigate to="/users/admin" replace />} />
                  <Route path="/settings/security" element={<SecuritySettingsPage />} />
                  <Route path="/settings/registration" element={<RegistrationSettingsPage />} />
                  <Route path="/users/registration" element={<UserRegistrationPage />} />
                  <Route path="/users/admin" element={<UserAdminPage />} />
                  <Route path="/users/groups" element={<UserGroupsPage />} />
                  <Route path="/users/roles" element={<UserRolesPage />} />
                  <Route path="/system/console" element={<WebConsolePage />} />
                  <Route path="/system/api" element={<ApiSettingsPage />} />
                  <Route path="/system/app-templates" element={<AppTemplatesPage />} />
                  <Route path="/system/ssl" element={<SslControlPage />} />
                  <Route path="/system/vpn" element={<VpnControlPage />} />
                  <Route path="/system/changelog" element={<ChangelogPage />} />
                  <Route path="/system/update" element={<Navigate to="/system/changelog" replace />} />
                  <Route path="/system/news" element={<NewsPage />} />
                  <Route path="/system/network" element={<NetworkPage />} />
                  <Route element={<RequireFeature feature="inter-instance-transfer" />}>
                    <Route path="/transfers" element={<TransfersPage />} />
                  </Route>
                  <Route element={<RequireFeature feature="delete-protection" />}>
                    <Route element={<RequirePermission permission={DELETE_PROTECTION_PERMISSION} />}>
                      <Route path="/system/delete-protection" element={<DeleteProtectionPage />} />
                    </Route>
                  </Route>
                  <Route path="/system/logs" element={<SystemLogsPage />} />
                  <Route path="/system/audit-log" element={<AuditLogPage />} />
                </Route>
                {/* Asset Management Center is capability-gated in-page (admins
                    + ACL `media.library.use` users), not admin-only. */}
                <Route path="/content/media" element={<MediaLibraryPage />} />
                {/* Media Requests is granted, not inherited: an admin assigns
                    it, the same way the Asset Management Center is assigned. It
                    therefore sits outside the admin block and gates on the same
                    permission the API gates on, so a grant actually works.
                    Rendered on its own canvas — it has no WSDashboard or
                    media-server dependency, and the server imposes neither. */}
                <Route element={<RequirePermission permission="admin.requests.read" />}>
                  {/* An admin gets the streaming settings flyout hosted in place
                      (so QuickNav's settings item opens it here with no
                      navigation, exactly like Media Portal); a non-admin grantee
                      renders a bare Outlet — the pages exactly as before, no
                      flyout hooks firing. */}
                  <Route element={<StreamingFlyoutHost adminOnly />}>
                    <Route element={<MediaCanvasLayout standalone labelKey="mediaRequests" />}>
                      <Route path="/streaming/dashboard/control-center/requests" element={<MediaRequestsPage />} />
                      <Route path="/streaming/dashboard/control-center/requests/:providerId/:ownerUsername/:mediaType/:requestId" element={<MediaRequestDetailPage />} />
                    </Route>
                  </Route>
                </Route>
                {/* The old path stays reachable so existing links and bookmarks
                    still land somewhere real. */}
                <Route
                  path="/streaming/requests"
                  element={<Navigate to="/streaming/dashboard/control-center/requests" replace />}
                />
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
        </Suspense>
      </AuthProvider>
    </DialogProvider>
  </ThemeProvider></LanguageProvider>
);

export default App;
